import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, type ReactNode } from "react";
import { ApiError } from "@hostdock/sdk";
import type { AdminSessionInfo, Permission } from "@hostdock/types";
import { api, client, SESSION_KEY } from "./api";

interface SessionCtx {
  session: AdminSessionInfo;
  can(permission: Permission): boolean;
  refresh(): Promise<void>;
  logout(): Promise<void>;
}
const Ctx = createContext<SessionCtx | null>(null);

export function useSessionQuery() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await api.account.session();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function useSetSession() {
  const qc = useQueryClient();
  return useCallback(
    (session: AdminSessionInfo | null) => {
      client.setCsrfToken(session?.csrfToken);
      qc.setQueryData(SESSION_KEY, session);
    },
    [qc],
  );
}

export function SessionProvider({ session, children }: { session: AdminSessionInfo; children: ReactNode }) {
  const qc = useQueryClient();
  useEffect(() => client.setCsrfToken(session.csrfToken), [session.csrfToken]);
  const can = useCallback((p: Permission) => session.permissions.includes(p), [session.permissions]);
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: SESSION_KEY }), [qc]);
  const logout = useCallback(async () => {
    try {
      await api.account.logout();
    } finally {
      client.setCsrfToken(null);
      // Keep the session query (the app observes it) and drop everything else.
      qc.setQueryData(SESSION_KEY, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] });
    }
  }, [qc]);
  return <Ctx.Provider value={{ session, can, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}
